import { Link } from 'react-router-dom'
import { TrendingDown, Home, Activity, CreditCard, Package } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const MAGNETS: Array<{ id: string; title: string; subtitle: string; industry: string; Icon: LucideIcon; abstract: string }> = [
  {
    id: 'saas-churn-survey-lies',
    title: 'The 7 Most Common Churn Survey Lies in B2B SaaS',
    subtitle: 'Why your exit surveys are probably telling you the wrong story',
    industry: 'SaaS',
    Icon: TrendingDown,
    abstract: 'Exit surveys consistently overstate price sensitivity and understate engagement failure. Learn how to calibrate your interpretation with behavioral evidence.',
  },
  {
    id: 'insurance-renewal-trust',
    title: 'What Insurance Renewal Copy Actually Moves Trust',
    subtitle: 'From real policyholder reactions, analyzed by ensemble behavioral AI',
    industry: 'Insurance',
    Icon: Home,
    abstract: 'Five communication patterns that measurably improve renewal trust — and what creates NAIC complaint risk.',
  },
  {
    id: 'healthcare-comprehension-gap',
    title: 'The Literacy Gap Killing Member Retention',
    subtitle: 'Healthcare benefits communication fails 60% of members before they read it',
    industry: 'Healthcare',
    Icon: Activity,
    abstract: 'The comprehension failures driving member disenrollment and the communication patterns that close the gap.',
  },
  {
    id: 'financial-services-plain-language',
    title: 'Plain-Language Wins in Financial Services Messaging',
    subtitle: 'CFPB data, FINRA complaints, and what actually moves consumer trust',
    industry: 'Financial Services',
    Icon: CreditCard,
    abstract: 'Where regulatory language requirements and consumer trust drivers converge — and how to optimize for both.',
  },
  {
    id: 'consumer-pack-design-signals',
    title: 'What Makes a Pack Design Feel Premium vs. Cheap',
    subtitle: 'The behavioral signals decoded (and the FTC rules that matter)',
    industry: 'Consumer Brands',
    Icon: Package,
    abstract: 'Five measurable signals that drive premium packaging perception — three behavioral, two regulatory.',
  },
]

export function ResearchPage() {
  return (
    <div className="min-h-screen bg-off-white">
      <header className="border-b border-border-gray bg-paper px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-base font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>echosome</Link>
        <Link to="/signup" className="text-sm bg-echo-indigo text-white px-4 py-2 rounded-lg hover:bg-echo-indigo/90 transition-colors">
          Try free →
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-deep-ink mb-3" style={{ letterSpacing: '-0.03em' }}>Research Library</h1>
          <p className="text-base text-slate-600 max-w-2xl">
            Evidence-grounded reports on behavioral bias, regulatory compliance, and communication effectiveness across five industries.
            Free to read. No email required for the first 30%.
          </p>
        </div>

        <div className="space-y-4">
          {MAGNETS.map(m => (
            <Link key={m.id} to={`/research/${m.id}`} className="block">
              <div className="bg-paper border border-border-gray rounded-2xl p-6 hover:border-echo-indigo/30 hover:shadow-sm transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-echo-indigo/10 flex items-center justify-center shrink-0"><m.Icon className="w-5 h-5 text-echo-indigo" strokeWidth={1.5} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-echo-indigo font-medium bg-echo-indigo/10 px-2 py-0.5 rounded-full">{m.industry}</span>
                    </div>
                    <h2 className="text-base font-semibold text-deep-ink mb-1">{m.title}</h2>
                    <p className="text-sm text-slate-500 mb-2">{m.subtitle}</p>
                    <p className="text-sm text-slate-600">{m.abstract}</p>
                  </div>
                  <div className="text-slate-400 shrink-0 text-sm">Read →</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  )
}
